import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/use-toast";
import { BookingStatus as ImportedBookingStatus } from "@/hooks/useBookingStatuses";

export interface BookingStatus {
  id: string;
  status_key: string;
  display_name: string;
  description?: string;
  color: string;
  sort_order: number;
  category: string;
  is_active: boolean;
  created_at: string;
}

export interface CreateBookingStatusData {
  status_key: string;
  display_name: string;
  description?: string;
  color: string;
  sort_order?: number;
  category: string;
}

export interface UpdateBookingStatusData extends Partial<CreateBookingStatusData> {
  is_active?: boolean;
}

// REMOVED: CreateStatusTransitionData interface
// Open transitions: no transition table needed

// Event Categories  
export interface EventCategory {
  id: string;
  category_key: string;
  display_name: string;
  description?: string;
  sort_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateEventCategoryData {
  category_key: string;
  display_name: string;
  description?: string;
  sort_order: number;
}

export interface UpdateEventCategoryData {
  display_name?: string;
  description?: string;
  sort_order?: number;
  is_active?: boolean;
}

// Event Categories Hooks
export const useEventCategories = () => {
  return useQuery({
    queryKey: ['eventCategories'],
    queryFn: async (): Promise<EventCategory[]> => {
      console.log('[EVENT-CATEGORIES] Fetching event categories');
      const { data, error } = await supabase
        .from('event_categories')
        .select('*')
        .eq('is_active', true)
        .order('sort_order');

      if (error) {
        console.error('[EVENT-CATEGORIES] Error fetching categories:', error);
        throw error;
      }
      
      console.log('[EVENT-CATEGORIES] Fetched categories:', data);
      return data as EventCategory[];
    },
  });
};

export const useCreateEventCategory = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (categoryData: CreateEventCategoryData): Promise<EventCategory> => {
      const { data, error } = await supabase
        .from('event_categories')
        .insert([categoryData])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['eventCategories'] });
      toast({
        title: "Success",
        description: "Event category created successfully",
        variant: "success",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: `Failed to create event category: ${error.message}`,
        variant: "destructive",
      });
    },
  });
};

export const useUpdateEventCategory = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: UpdateEventCategoryData }): Promise<EventCategory> => {
      const { data, error } = await supabase
        .from('event_categories')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['eventCategories'] });
      toast({
        title: "Success",
        description: "Event category updated successfully",
        variant: "success",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error", 
        description: `Failed to update event category: ${error.message}`,
        variant: "destructive",
      });
    },
  });
};

export const useDeleteEventCategory = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (categoryId: string): Promise<void> => {
      const { error } = await supabase
        .from('event_categories')
        .delete()
        .eq('id', categoryId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['eventCategories'] });
      toast({
        title: "Success",
        description: "Event category deleted successfully",
        variant: "success",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error", 
        description: `Failed to delete event category: ${error.message}`,
        variant: "destructive",
      });
    },
  });
};

// Booking Statuses Hooks
export function useBookingStatuses() {
  return useQuery({
    queryKey: ["booking-statuses"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("booking_statuses")
        .select("*")
        .order("sort_order", { ascending: true });

      if (error) throw error;
      return data as BookingStatus[];
    },
  });
}

export function useCreateBookingStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (statusData: CreateBookingStatusData) => {
      const { data, error } = await supabase
        .from("booking_statuses")
        .insert([statusData])
        .select()
        .single();

      if (error) throw error;
      return data as BookingStatus;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["booking-statuses"] });
      toast({
        title: "Success",
        description: "Booking status created successfully",
        variant: "success",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: `Failed to create booking status: ${error.message}`,
        variant: "destructive",
      });
    },
  });
}

export function useUpdateBookingStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      id, 
      updates 
    }: { 
      id: string; 
      updates: UpdateBookingStatusData;
    }) => {
      console.log('[UPDATE-BOOKING-STATUS] Attempting to update status:', { id, updates });
      
      const { data, error } = await supabase
        .from("booking_statuses")
        .update(updates)
        .eq("id", id)
        .select()
        .maybeSingle();

      console.log('[UPDATE-BOOKING-STATUS] Result:', { data, error });
      
      if (error) throw error;
      if (!data) throw new Error("Booking status not found");
      return data as BookingStatus;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["booking-statuses"] });
      toast({
        title: "Success", 
        description: "Booking status updated successfully",
        variant: "success",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: `Failed to update booking status: ${error.message}`,
        variant: "destructive",
      });
    },
  });
}

export function useUpdateBookingStatusOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (statusUpdates: { id: string; sort_order: number }[]) => {
      const updates = statusUpdates.map(update => 
        supabase
          .from("booking_statuses")
          .update({ sort_order: update.sort_order })
          .eq("id", update.id)
      );

      const results = await Promise.all(updates);
      
      // Check for errors
      const errors = results.filter(result => result.error);
      if (errors.length > 0) {
        throw new Error(`Failed to update ${errors.length} booking statuses`);
      }

      return results;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["booking-statuses"] });
      toast({
        title: "Success",
        description: "Booking status order updated successfully",
        variant: "success",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: `Failed to update booking status order: ${error.message}`,
        variant: "destructive",
      });
    },
  });
}

export function useDeleteBookingStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("booking_statuses")
        .delete()
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["booking-statuses"] });
      toast({
        title: "Success",
        description: "Booking status deleted successfully", 
        variant: "success",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: `Failed to delete booking status: ${error.message}`,
        variant: "destructive",
      });
    },
  });
}

// Status Key Update Hooks
export interface StatusKeyUpdateResult {
  success: boolean;
  old_status_key?: string;
  new_status_key?: string;
  affected_records?: {
    bookings: number;
    transitions: number;
    from_transitions: number;
    to_transitions: number;
    from_flows: number;
    to_flows: number;
    from_queue: number;
    to_queue: number;
  };
  error?: string;
  details?: string;
}

export interface StatusKeyUsage {
  in_use: boolean;
  active_bookings_count: number; // Will be converted from bigint
}

export function useCheckStatusKeyUsage() {
  return useMutation({
    mutationFn: async (statusKey: string): Promise<StatusKeyUsage> => {
      const { data, error } = await supabase.rpc('check_status_key_in_use', {
        p_status_key: statusKey
      });

      if (error) throw error;
      
      // Convert bigint to number for the interface
      const result = data[0];
      return {
        in_use: result.in_use,
        active_bookings_count: Number(result.active_bookings_count)
      } as StatusKeyUsage;
    },
  });
}

export function useUpdateStatusKey() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      statusId, 
      newStatusKey 
    }: { 
      statusId: string; 
      newStatusKey: string;
    }): Promise<StatusKeyUpdateResult> => {
      const { data, error } = await supabase.rpc('update_booking_status_key', {
        p_status_id: statusId,
        p_new_status_key: newStatusKey
      });

      if (error) throw error;
      
      // Ensure we have a valid result structure
      if (!data || typeof data !== 'object') {
        throw new Error('Invalid response from server');
      }
      
      return (data as unknown) as StatusKeyUpdateResult;
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["booking-statuses"] });
      queryClient.invalidateQueries({ queryKey: ["status-transitions"] });
      queryClient.invalidateQueries({ queryKey: ["event-flows"] });
      
      if (result.success) {
        toast({
          title: "Success",
          description: `Status key updated from "${result.old_status_key}" to "${result.new_status_key}"`,
          variant: "success",
        });
      } else {
        toast({
          title: "Error",
          description: result.error || "Failed to update status key",
          variant: "destructive",
        });
      }
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: `Failed to update status key: ${error.message}`,
        variant: "destructive",
      });
    },
  });
}

// REMOVED: All Status Transitions Hooks
// - useStatusTransitions()
// - useCreateStatusTransition()
// - useDeleteStatusTransition()
// - useUpdateStatusTransitionOrder()
// Open transitions architecture eliminates the need for these

// Event Flows Management (open architecture - direct status columns)
export interface EventFlow {
  id: string;
  name: string;
  description?: string;
  from_status: string | null; // NULL means "any status"
  to_status: string; // Deprecated: kept for backward compatibility
  target_statuses: string[]; // NEW: Array of target status keys
  from_status_info?: { display_name: string; color: string };
  to_status_info?: { display_name: string; color: string }; // Shows first target
  template_id: string;
  template_category?: string;
  channels: string[];
  recipient_config: {
    types: string[];
    custom_emails?: string[];
  };
  delay_minutes: number;
  priority: number;
  conditions?: Record<string, any>;
  is_active: boolean;
  org_id?: string;
  created_at: string;
  updated_at: string;
}

export interface CreateEventFlowData {
  name: string;
  description?: string;
  from_status?: string | null; // NULL means "any status"
  target_statuses: string[]; // NEW: Required array of status keys
  template_id: string;
  channels: string[];
  recipient_config: {
    types: string[];
    custom_emails?: string[];
  };
  delay_minutes: number;
  priority: number;
  conditions: Record<string, any>;
  org_id?: string;
}

export interface UpdateEventFlowData {
  name?: string;
  description?: string;
  from_status?: string | null; // NULL means "any status"
  target_statuses?: string[]; // NEW: Array of status keys
  template_id?: string;
  channels?: string[];
  recipient_config?: {
    types: string[];
    custom_emails?: string[];
  };
  delay_minutes?: number;
  priority?: number;
  conditions?: Record<string, any>;
  is_active?: boolean;
}

// Event Flows Hooks
export function useEventFlows() {
  return useQuery({
    queryKey: ["event-flows"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("event_flows")
        .select(`
          *,
          template:communication_templates(
            id,
            name,
            category
          )
        `)
        .order("priority", { ascending: true })
        .order("created_at", { ascending: false });

      if (error) throw error;
      
      // Enrich with status info
      const enrichedData = await Promise.all((data || []).map(async (flow: any) => {
        const statusPromises = [];
        
        // Get from_status info if exists
        if (flow.from_status) {
          statusPromises.push(
            supabase
              .from('booking_statuses')
              .select('display_name, color')
              .eq('status_key', flow.from_status)
              .maybeSingle()
              .then(({ data }) => ({ from_status_info: data }))
          );
        } else {
          statusPromises.push(Promise.resolve({ from_status_info: null }));
        }
        
        // Get to_status info (use first target status for display)
        if (flow.target_statuses && flow.target_statuses.length > 0) {
          statusPromises.push(
            supabase
              .from('booking_statuses')
              .select('display_name, color')
              .eq('status_key', flow.target_statuses[0])
              .maybeSingle()
              .then(({ data }) => ({ to_status_info: data }))
          );
        } else if (flow.to_status) {
          // Fallback for backward compatibility
          statusPromises.push(
            supabase
              .from('booking_statuses')
              .select('display_name, color')
              .eq('status_key', flow.to_status)
              .maybeSingle()
              .then(({ data }) => ({ to_status_info: data }))
          );
        } else {
          statusPromises.push(Promise.resolve({ to_status_info: null }));
        }
        
        const [fromInfo, toInfo] = await Promise.all(statusPromises);
        
        return {
          ...flow,
          ...fromInfo,
          ...toInfo
        };
      }));
      
      return enrichedData as any[];
    },
  });
}

export function useCreateEventFlow() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (flowData: CreateEventFlowData) => {
      // Get the template's category
      const { data: template } = await supabase
        .from("communication_templates")
        .select("category")
        .eq("id", flowData.template_id)
        .single();

      const insertData: any = {
        name: flowData.name,
        description: flowData.description,
        from_status: flowData.from_status || null, // NULL means "any status"
        target_statuses: flowData.target_statuses, // NEW: Array of statuses
        template_id: flowData.template_id,
        channels: flowData.channels,
        recipient_config: flowData.recipient_config,
        delay_minutes: flowData.delay_minutes,
        priority: flowData.priority,
        conditions: flowData.conditions,
        org_id: flowData.org_id,
        template_category: template?.category || "unknown"
      };

      const { data: result, error } = await supabase
        .from("event_flows")
        .insert([insertData])
        .select()
        .single();

      if (error) throw error;
      
      // Enrich with status info
      const flow = result as any;
      const statusPromises = [];
      
      if (flow.from_status) {
        statusPromises.push(
          supabase
            .from('booking_statuses')
            .select('display_name, color')
            .eq('status_key', flow.from_status)
            .maybeSingle()
            .then(({ data }) => ({ from_status_info: data }))
        );
      } else {
        statusPromises.push(Promise.resolve({ from_status_info: null }));
      }
      
      statusPromises.push(
        supabase
          .from('booking_statuses')
          .select('display_name, color')
          .eq('status_key', flow.target_statuses?.[0] || flow.to_status)
          .maybeSingle()
          .then(({ data }) => ({ to_status_info: data }))
      );
      
      const [fromStatusData, toStatusData] = await Promise.all(statusPromises);
      
      return {
        ...flow,
        ...fromStatusData,
        ...toStatusData
      } as any;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["event-flows"] });
      toast({
        title: "Success",
        description: "Event flow created successfully",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: `Failed to create event flow: ${error.message}`,
        variant: "destructive",
      });
    },
  });
}

export function useUpdateEventFlow() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      id, 
      updates 
    }: { 
      id: string; 
      updates: UpdateEventFlowData;
    }) => {
      // Handle empty string from_status as NULL
      const updateData: any = { ...updates };
      if (updateData.from_status === '') {
        updateData.from_status = null;
      }

      const { data, error } = await supabase
        .from("event_flows")
        .update(updateData)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data as any;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["event-flows"] });
      toast({
        title: "Success",
        description: "Event flow updated successfully",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: `Failed to update event flow: ${error.message}`,
        variant: "destructive",
      });
    },
  });
}

export function useUpdateEventFlowPriority() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (flowUpdates: { id: string; priority: number }[]) => {
      const updates = flowUpdates.map(update => 
        supabase
          .from("event_flows")
          .update({ priority: update.priority })
          .eq("id", update.id)
      );

      const results = await Promise.all(updates);
      
      // Check for errors
      const errors = results.filter(result => result.error);
      if (errors.length > 0) {
        throw new Error(`Failed to update ${errors.length} event flows`);
      }

      return results;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["event-flows"] });
      toast({
        title: "Success",
        description: "Event flow priority updated successfully",
        variant: "success",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: `Failed to update event flow priority: ${error.message}`,
        variant: "destructive",
      });
    },
  });
}

export function useDeleteEventFlow() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("event_flows")
        .delete()
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["event-flows"] });
      toast({
        title: "Success",
        description: "Event flow deleted successfully",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: `Failed to delete event flow: ${error.message}`,
        variant: "destructive",
      });
    },
  });
}

// Recipient Checking for Event Flows
export interface RecipientCheckResult {
  recipients: {
    type: string;
    email?: string;
    phone?: string;
    name?: string;
    has_email: boolean;
    has_phone: boolean;
  }[];
  summary: {
    total_count: number;
    email_count: number;
    phone_count: number;
    missing_email_count: number;
    missing_phone_count: number;
  };
}

export function useCheckRecipients() {
  return useMutation({
    mutationFn: async ({ 
      recipientConfig, 
      bookingId,
      orgId 
    }: { 
      recipientConfig: { types: string[]; custom_emails?: string[] }; 
      bookingId?: string;
      orgId?: string;
    }): Promise<RecipientCheckResult> => {
      // Use a default booking ID if none provided (for preview purposes)
      let testBookingId = bookingId;
      
      if (!testBookingId) {
        // Get the first available booking for testing purposes
        const { data: bookings } = await supabase
          .from('bookings')
          .select('id')
          .limit(1);
        
        if (bookings && bookings.length > 0) {
          testBookingId = bookings[0].id;
        } else {
          throw new Error('No bookings available for recipient testing');
        }
      }

      const { data, error } = await supabase.rpc('resolve_event_recipients', {
        p_recipient_config: recipientConfig,
        p_booking_id: testBookingId,
        p_org_id: orgId
      });

      if (error) throw error;
      return data as unknown as RecipientCheckResult;
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: `Failed to check recipients: ${error.message}`,
        variant: "destructive",
      });
    },
  });
}

// Communication Templates Management
export interface CommunicationTemplate {
  id: string;
  name: string;
  description?: string;
  category: string;
  subject_template: string;
  content_template: string;
  supported_channels: string[];
  variables: Record<string, any>;
  version: number;
  is_active: boolean;
  org_id?: string;
  created_at: string;
  updated_at: string;
}

export interface CreateCommunicationTemplateData {
  name: string;
  description?: string;
  category: string;
  subject_template: string;
  content_template: string;
  supported_channels?: string[];
  variables?: Record<string, any>;
  org_id?: string;
}

export interface UpdateCommunicationTemplateData extends Partial<CreateCommunicationTemplateData> {
  is_active?: boolean;
  version?: number;
}

// Communication Templates Hooks
export function useCommunicationTemplates() {
  return useQuery({
    queryKey: ["communication-templates"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("communication_templates")
        .select("*")
        .order("category", { ascending: true })
        .order("name", { ascending: true });

      if (error) throw error;
      return data as CommunicationTemplate[];
    },
  });
}

export function useCreateCommunicationTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (templateData: CreateCommunicationTemplateData) => {
      const data = {
        supported_channels: ["email"],
        variables: {},
        version: 1,
        ...templateData,
      };

      const { data: result, error } = await supabase
        .from("communication_templates")
        .insert([data])
        .select()
        .single();

      if (error) throw error;
      return result as CommunicationTemplate;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["communication-templates"] });
      toast({
        title: "Success",
        description: "Communication template created successfully",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: `Failed to create template: ${error.message}`,
        variant: "destructive",
      });
    },
  });
}

export function useUpdateCommunicationTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      id, 
      updates 
    }: { 
      id: string; 
      updates: UpdateCommunicationTemplateData;
    }) => {
      const { data, error } = await supabase
        .from("communication_templates")
        .update(updates)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data as CommunicationTemplate;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["communication-templates"] });
      toast({
        title: "Success",
        description: "Communication template updated successfully",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: `Failed to update template: ${error.message}`,
        variant: "destructive",
      });
    },
  });
}

export function useDeleteCommunicationTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("communication_templates")
        .delete()
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["communication-templates"] });
      toast({
        title: "Success",
        description: "Communication template deleted successfully",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: `Failed to delete template: ${error.message}`,
        variant: "destructive",
      });
    },
  });
}

// Event Queue & Monitoring Management
interface EventQueueItem {
  id: string;
  event_flow_id: string;
  booking_id: string;
  from_status?: string;
  to_status: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  scheduled_at: string;
  processed_at?: string;
  retry_count: number;
  max_retries: number;
  error_message?: string;
  event_data: Record<string, any>;
  created_at: string;
  updated_at: string;
}

interface EventQueueStats {
  pending: number;
  processing: number;
  completed_today: number;
  failed_today: number;
  total_today: number;
}

interface OrchestrationLog {
  id: string;
  booking_id: string;
  event_flow_id?: string;
  event_queue_id?: string;
  template_category: string;
  channel: string;
  recipient_type: string;
  recipient_email?: string;
  status: string;
  sent_at?: string;
  delivered_at?: string;
  opened_at?: string;
  clicked_at?: string;
  delivery_status?: string;
  error_message?: string;
  provider_response?: Record<string, any>;
  created_at: string;
}

export function useEventQueue() {
  return useQuery({
    queryKey: ['event-queue'],
    queryFn: async (): Promise<EventQueueItem[]> => {
      const { data, error } = await supabase
        .from('event_queue')
        .select('*')
        .order('scheduled_at', { ascending: true })
        .limit(100);
      
      if (error) throw error;
      return (data || []) as EventQueueItem[];
    },
    refetchInterval: 15000, // Refetch every 15 seconds
  });
}

export function useEventQueueStats() {
  return useQuery({
    queryKey: ['event-queue-stats'],
    queryFn: async (): Promise<EventQueueStats> => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const [pending, processing, completedToday, failedToday] = await Promise.all([
        supabase
          .from('event_queue')
          .select('id', { count: 'exact' })
          .eq('status', 'pending'),
        supabase
          .from('event_queue')
          .select('id', { count: 'exact' })
          .eq('status', 'processing'),
        supabase
          .from('event_queue')
          .select('id', { count: 'exact' })
          .eq('status', 'completed')
          .gte('processed_at', today.toISOString()),
        supabase
          .from('event_queue')
          .select('id', { count: 'exact' })
          .eq('status', 'failed')
          .gte('updated_at', today.toISOString())
      ]);
      
      return {
        pending: pending.count || 0,
        processing: processing.count || 0,
        completed_today: completedToday.count || 0,
        failed_today: failedToday.count || 0,
        total_today: (completedToday.count || 0) + (failedToday.count || 0)
      };
    },
    refetchInterval: 30000, // Refetch every 30 seconds
  });
}

export function useRetryEvent() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (eventId: string): Promise<void> => {
      const { error } = await supabase
        .from('event_queue')
        .update({ 
          status: 'pending', 
          error_message: null,
          updated_at: new Date().toISOString()
        })
        .eq('id', eventId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['event-queue'] });
      queryClient.invalidateQueries({ queryKey: ['event-queue-stats'] });
      toast({
        title: "Success",
        description: "Event queued for retry",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: `Failed to retry event: ${error.message}`,
        variant: "destructive",
      });
    },
  });
}

export function useCancelEvent() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (eventId: string): Promise<void> => {
      const { error } = await supabase
        .from('event_queue')
        .update({ 
          status: 'failed',
          error_message: 'Cancelled by admin',
          updated_at: new Date().toISOString()
        })
        .eq('id', eventId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['event-queue'] });
      queryClient.invalidateQueries({ queryKey: ['event-queue-stats'] });
      toast({
        title: "Success",
        description: "Event cancelled successfully",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: `Failed to cancel event: ${error.message}`,
        variant: "destructive",
      });
    },
  });
}

export function useOrchestrationLogs() {
  return useQuery({
    queryKey: ['orchestration-logs'],
    queryFn: async (): Promise<OrchestrationLog[]> => {
      const { data, error } = await supabase
        .from('orchestration_logs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(200);
      
      if (error) throw error;
      return (data || []) as OrchestrationLog[];
    },
    refetchInterval: 30000, // Refetch every 30 seconds
  });
}

export function useManualEventProcessing() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (): Promise<void> => {
      const { error } = await supabase.functions.invoke('manual-event-processing');
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['event-queue'] });
      queryClient.invalidateQueries({ queryKey: ['event-queue-stats'] });
      queryClient.invalidateQueries({ queryKey: ['orchestration-logs'] });
      toast({
        title: "Success",
        description: "Manual event processing initiated",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: `Failed to process events: ${error.message}`,
        variant: "destructive",
      });
    },
  });
}

// System Health Management
interface SystemHealth {
  id: string;
  service_name: string;
  status: 'healthy' | 'warning' | 'critical' | 'maintenance';
  last_check: string;
  response_time_ms: number;
  error_count: number;
  uptime_percentage: number;
  metadata: Record<string, any>;
  created_at: string;
  updated_at: string;
}

interface SystemMetrics {
  id: string;
  metric_name: string;
  metric_value: number;
  metric_unit: string;
  threshold_warning: number;
  threshold_critical: number;
  recorded_at: string;
  metadata: Record<string, any>;
}

interface MaintenanceWindow {
  id: string;
  title: string;
  description: string;
  start_time: string;
  end_time: string;
  status: 'scheduled' | 'active' | 'completed' | 'cancelled';
  affected_services: string[];
  created_by: string;
  created_at: string;
  updated_at: string;
}

interface CreateMaintenanceWindowData {
  title: string;
  description: string;
  start_time: string;
  end_time: string;
  affected_services: string[];
}

export function useSystemHealth() {
  return useQuery({
    queryKey: ['system-health'],
    queryFn: async (): Promise<SystemHealth[]> => {
      const { data, error } = await supabase
        .from('system_health')
        .select('*')
        .order('service_name');
      
      if (error) throw error;
      return (data || []) as SystemHealth[];
    },
    refetchInterval: 30000, // Refetch every 30 seconds
    staleTime: 10000, // Consider data stale after 10 seconds
  });
}

export function useSystemMetrics() {
  return useQuery({
    queryKey: ['system-metrics'],
    queryFn: async (): Promise<SystemMetrics[]> => {
      const { data, error } = await supabase
        .from('system_metrics')
        .select('*')
        .gte('recorded_at', new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString()) // Last 24 hours
        .order('recorded_at', { ascending: false })
        .limit(100);
      
      if (error) throw error;
      return (data || []) as SystemMetrics[];
    },
    refetchInterval: 60000, // Refetch every minute
  });
}

export function useMaintenanceWindows() {
  return useQuery({
    queryKey: ['maintenance-windows'],
    queryFn: async (): Promise<MaintenanceWindow[]> => {
      const { data, error } = await supabase
        .from('maintenance_windows')
        .select('*')
        .order('start_time', { ascending: false });
      
      if (error) throw error;
      return (data || []) as MaintenanceWindow[];
    },
  });
}

export function useCreateMaintenanceWindow() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (maintenanceData: CreateMaintenanceWindowData): Promise<MaintenanceWindow> => {
      const { data, error } = await supabase
        .from('maintenance_windows')
        .insert([{
          ...maintenanceData,
          status: 'scheduled',
          created_by: (await supabase.auth.getUser()).data.user?.id || ''
        }])
        .select()
        .single();
      
      if (error) throw error;
      return data as MaintenanceWindow;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenance-windows'] });
      toast({
        title: "Success",
        description: "Maintenance window scheduled successfully",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: `Failed to schedule maintenance window: ${error.message}`,
        variant: "destructive",
      });
    },
  });
}

export function useUpdateMaintenanceWindow() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: MaintenanceWindow['status'] }): Promise<MaintenanceWindow> => {
      const { data, error } = await supabase
        .from('maintenance_windows')
        .update({ status, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data as MaintenanceWindow;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenance-windows'] });
      toast({
        title: "Success",
        description: "Maintenance window updated successfully",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: `Failed to update maintenance window: ${error.message}`,
        variant: "destructive",
      });
    },
  });
}

export function useRunHealthCheck() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (serviceName?: string): Promise<void> => {
      // This would typically call an edge function to run health checks
      const { error } = await supabase.functions.invoke('run-health-check', {
        body: { service_name: serviceName }
      });
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['system-health'] });
      toast({
        title: "Success",
        description: "Health check initiated successfully",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: `Failed to run health check: ${error.message}`,
        variant: "destructive",
      });
    },
  });
}

export function useToggleMaintenanceMode() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (enabled: boolean): Promise<void> => {
      const { error } = await supabase.functions.invoke('toggle-maintenance-mode', {
        body: { enabled }
      });
      
      if (error) throw error;
    },
    onSuccess: (_, enabled) => {
      queryClient.invalidateQueries({ queryKey: ['system-health'] });
      toast({
        title: "Success",
        description: `Maintenance mode ${enabled ? 'enabled' : 'disabled'} successfully`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: `Failed to toggle maintenance mode: ${error.message}`,
        variant: "destructive",
      });
    },
  });
}
